import React, { Fragment, useState, forwardRef } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import FastRewindIcon from '@material-ui/icons/FastRewind';
import FastForwardIcon from '@material-ui/icons/FastForward';
import Tooltip from '@material-ui/core/Tooltip';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import WarningIcon from '@mui/icons-material/ErrorOutlineRounded';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import FullScreenIcon from '@material-ui/icons/Fullscreen';
import LoopIcon from '@material-ui/icons/Loop';
import ExternalIcon from '@material-ui/icons/Launch';
import PiPIcon from '@material-ui/icons/PictureInPictureAlt';
import PlaybackIcon from '@mui/icons-material/SlowMotionVideo';
import Slider from '@material-ui/core/Slider';
import Popover from '@material-ui/core/Popover';

import {images} from '../../constants'

let LoaderSize = 50;
const useStyles = makeStyles({
  controlsWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: '5px',
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    zIndex: 1,
    borderRadius:'0.5rem'
  },

  controlIcons: {
    color: '#777',
    fontSize: 50,
    transform: 'scale(0.9)',
    '&:hover': {
      color: '#fff',
      transform: 'scale(1)',
    },
  },

  bottomIcons: {
    color: '#999',
    '&:hover': {
      color: '#fff',
    },
  },

  volumeSlider: {
    color: '#0142C2',
    width: 100,
    margin: 'auto',
  },
  logo: {
    width: 60,
    height: 40,
    opacity: 0.4,
    userSelect: 'none',
    objectFit:'cover',
    filter:'grayscale(1)',
  },
  logoMobile: {
    width: 30,
    height: 20,
    opacity: 0.4,
    userSelect: 'none',
    objectFit:'cover',
    filter:'grayscale(1)',
  },
  activeIcon: {
    color: '#fff',
  },
  inactiveIcon: {
    color: '#999',
  },
  loader: {
    top: `calc(50% - ${LoaderSize / 2}px)`,
    left: `calc(50% - ${LoaderSize / 2}px)`,
    position: 'absolute',
    color: '#0142C2 !important',
  },
  loaderText: {
    top: `calc(65% - ${LoaderSize / 2}px)`,
    left: `calc(48.5% - ${LoaderSize / 2}px)`,
    position: 'absolute',
    color: '#fff',
    fontFamily: 'poppins',
  },
  loaderTextFull: {
    top: `calc(57% - ${LoaderSize / 2}px)`,
    left: `calc(49% - ${LoaderSize / 2}px)`,
    position: 'absolute',
    color: '#fff',
    fontFamily: 'poppins',
  },
  errorIcon: {
    top: `calc(50% - ${LoaderSize / 2}px)`,
    left: `calc(52% - ${LoaderSize / 2}px)`,
    position: 'absolute',
    color: '#fff',
  },
  errorText: {
    top: `calc(60% - ${LoaderSize / 2}px)`,
    left: `calc(37.5% - ${LoaderSize / 2}px)`,
    position: 'absolute',
    color: '#fff',
    fontSize: '15px',
    fontFamily: 'poppins',
  },
  errorSubText: {
    top: `calc(65% - ${LoaderSize / 2}px)`,
    left: `calc(47.5%  - ${LoaderSize / 2}px)`,
    position: 'absolute',
    color: '#999',
    fontSize: '12px',
    fontFamily: 'poppins',
  },
  errorIconFull: {
    top: `calc(50% - ${LoaderSize / 2}px)`,
    left: `calc(50% - ${LoaderSize / 2}px)`,
    position: 'absolute',
    color: '#fff',
  },
  errorTextFull: {
    top: `calc(55% - ${LoaderSize / 2}px)`,
    left: `calc(41.1% - ${LoaderSize / 2}px)`,
    position: 'absolute',
    color: '#fff',
    fontSize: '15px',
    fontFamily: 'poppins',
  },
  errorSubTextFull: {
    top: `calc(58% - ${LoaderSize / 2}px)`,
    left: `calc(47%  - ${LoaderSize / 2}px)`,
    position: 'absolute',
    color: '#999',
    fontSize: '12px',
    fontFamily: 'poppins',
  },
});

const PrettoSlider = withStyles({
  root: {
    color: '#313bac',
    height: 8,
  },
  thumb: {
    height: 24,
    width: 24,
    backgroundColor: '#fff',
    border: '2px solid currentColor',
    marginTop: -8,
    marginLeft: -12,
    '&:focus, &:hover, &$active': {
      boxShadow: 'inherit',
    },
  },
  active: {},
  valueLabel: {
    left: 'calc(-50% + 4px)',
  },
  track: {
    height: 8,
    borderRadius: 4,
  },
  rail: {
    height: 8,
    borderRadius: 4,
  },
})(Slider);

export default forwardRef(
  (
    {
      onPlayPause,
      onRewind,
      onFastForward,
      onVolumeChange,
      onVolumeSeekUp,
      onPlaybackRateChange,
      onToggleFullScreen,
      onTogglePip,
      onMute,
      onSeek,
      onSeekMouseDown,
      onSeekMouseUp,
      onChangeDisplayFormat,
      onBookmark,
      onLoop,
      muted,
      playing,
      volume,
      playbackRate,
      played,
      elapsedTime,
      totalDuration,
      loop,
      buffering,
      error,
      fullscreen,
      videoUrl,
      isMobile
    },
    ref
  ) => {
    const classes = useStyles();

    const [anchorEl, setAnchorEl] = useState(null);

    const handlePopover = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'playbackrate-popover' : undefined;

    function ValueLabelComponent(props) {
      const { children, open, value } = props;

      return (
        <Tooltip open={open} enterTouchDelay={0} placement='top' title={value}>
          {children}
        </Tooltip>
      );
    }
    return (
      <div className={classes.controlsWrapper} ref={ref}>
        {/* #region Top Controls */}
        <Grid
          container
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          style={isMobile? {padding: 6 } :{ padding: 16 }}
        >
          <Grid item>
            <Typography
              variant='h5'
              style={{ color: '#fff', fontFamily: 'poppins' }}
            >
                  <img src={images.skFavicon} alt='SK' className={isMobile ? classes.logoMobile:classes.logo} />{' '}
              {/* DEL-CODE V2 */}
            </Typography>
          </Grid>

          <Grid item>
            {/* <Button
              onClick={onBookmark}
              variant='contained'
              style={{
                backgroundColor: '#313bac',
                color: '#fff',
                fontFamily: 'poppins',
              }}
              startIcon={<BookmarkIcon />}
            >
              Bookmark
            </Button> */}
            <a
              href={videoUrl}
              target='_blank'
              rel='noreferrer'
              style={{
                userSelect:'none',
                textDecoration:'none',
                outline:'none'
              }}
            >
              <IconButton
                style={{
                  // backgroundColor: '#313bac',
                  color: '#fff',
                  fontFamily: 'poppins',
                  marginLeft: '5px',
                  userSelect:'none',
                }}
                aria-label='External Player'
                component='span'
              >
                <ExternalIcon fontSize={isMobile ? 'small':''}/>
              </IconButton>
            </a>
          </Grid>
        </Grid>

        {/* #endregion */}

        {error ? (
          <Fragment>
            <WarningIcon
              fontSize='large'
              color='error'
              className={fullscreen ? classes.errorIconFull : classes.errorIcon}
            />
            <Typography
              className={fullscreen ? classes.errorTextFull : classes.errorText}
            >
              Failed to load media, please try again..
            </Typography>
            <Typography
              className={
                fullscreen ? classes.errorSubTextFull : classes.errorSubText
              }
            >
              Errorcode: 4036-62
            </Typography>
          </Fragment>
        ) : buffering ? (
          <Fragment>
            <CircularProgress
              // color='warning'
              size={LoaderSize}
              className={classes.loader}
            />
            <Typography
              className={
                fullscreen ? classes.loaderTextFull : classes.loaderText
              }
            >
              Buffering..
            </Typography>
          </Fragment>
        ) : (
          <Fragment>
            {/* #region Middle Controls */}
            <Grid
              container
              direction='row'
              alignItems='center'
              justifyContent='center'
            >
              <IconButton
                onClick={onRewind}
                className={classes.controlIcons}
                aria-label='rewind'
              >
                <FastRewindIcon fontSize={isMobile ? 'medium':'inherit'} />
              </IconButton>

              <IconButton
                onClick={onPlayPause}
                className={classes.controlIcons}
                aria-label='play-pause'
              >
                {playing ? (
                  <PauseIcon fontSize={isMobile ? 'medium':'inherit'} />
                ) : (
                  <PlayArrowIcon fontSize={isMobile ? 'medium':'inherit'} />
                )}
              </IconButton>

              <IconButton
                onClick={onFastForward}
                className={classes.controlIcons}
                aria-label='rewind'
              >
                <FastForwardIcon fontSize={isMobile ? 'medium':'inherit'} />
              </IconButton>
            </Grid>

            {/* #endregion */}
          </Fragment>
        )}

        {/* #region Bottom Controls */}
        <Grid
          container
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          style={isMobile? {padding: 0 } :{padding: 16 }}
        >
          {!isMobile && <Grid item xs={12}>
            <PrettoSlider
              // valueLabelDisplay='auto'
              ValueLabelComponent={(props) => (
                <ValueLabelComponent {...props} value={elapsedTime} />
              )}
              aria-label='pretto slider'
              min={0}
              max={100}
              value={played * 100}
              onChange={onSeek}
              onMouseDown={onSeekMouseDown}
              onChangeCommitted={onSeekMouseUp}
            />
          </Grid>}
 

          <Grid item>
            <Grid container alignitems='center' direction='row'>
              <IconButton onClick={onPlayPause} className={classes.bottomIcons}>
                {playing ? (
                  <PauseIcon fontSize={isMobile ? 'small':'large'} />
                ) : (
                  <PlayArrowIcon fontSize={isMobile ? 'small':'large'} />
                )}
              </IconButton>

              <IconButton onClick={onMute} className={classes.bottomIcons}>
                {muted ? (
                  <VolumeOffIcon fontSize={isMobile ? 'small':'large'}/>
                ) : (
                  <VolumeUpIcon fontSize={isMobile ? 'small':'large'} />
                )}
              </IconButton>

              {!isMobile &&   <Slider
                min={0}
                max={100}
                defaultValue={volume * 100}
                className={classes.volumeSlider}
                onChange={onVolumeChange}
                onChangeCommitted={onVolumeSeekUp}
              />}
           

              <Button
                onClick={onChangeDisplayFormat}
                variant='text'
                style={{ color: '#fff', marginLeft: 16 }}
              >
                <Typography style={{fontSize:isMobile && '10px'}}>
                  {elapsedTime}/{totalDuration}
                </Typography>
              </Button>
            </Grid>
          </Grid>

          <Grid item>
            {!isMobile &&  <Button
              onClick={handlePopover}
              variant='text'
              className={classes.bottomIcons}
            >
              <PlaybackIcon fontSize='large' style={{ marginRight: '2px' }} />
              <Typography>{playbackRate}X</Typography>
            </Button>}
           
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
            >
              <Grid container direction='column-reverse'>
                {[0.5, 1, 1.5, 2, 3, 4, 5].map((rate) => (
                  <Button
                    onClick={() => {
                      onPlaybackRateChange(rate);
                    }}
                    variant='text'
                  >
                    <Typography
                      // color={rate === playbackRate ? 'secondary' : 'default'}
                      style={{
                        color: rate === playbackRate ? '#313bac' : '#000',
                      }}
                    >
                      {rate}
                    </Typography>
                  </Button>
                ))}
              </Grid>
            </Popover>
            {!isMobile &&
            <>
            <IconButton
              onClick={onLoop}
              className={loop ? classes.activeIcon : classes.inactiveIcon}
            >
              <LoopIcon fontSize={isMobile ? 'small':'large'} />
            </IconButton>
                      
             <IconButton onClick={onTogglePip} className={classes.bottomIcons}>
              <PiPIcon fontSize={isMobile ? 'small':'large'} />
            </IconButton>
            </>}

            <IconButton
              onClick={onToggleFullScreen}
              className={classes.bottomIcons}
            >
              <FullScreenIcon fontSize={isMobile ? 'small':'large'} />
            </IconButton>
          </Grid>
        </Grid>

        {/* #endregion */}
      </div>
    );
  }
);